<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="computedTitle"
    dialog-width="50%"
    max-height="80vh"
    body-height="70vh"
    @agree="agreeAction"
    :loading="loading"
    :is-valid="isValid"
    :agree-text="computedAgree"
  >
    <template #body>
      <template v-if="isFetched">
        <v-col cols="12">
          <v-form ref="form" v-model="isValid">
            <v-row no-gutters>
              <v-col cols="9">
                <main-input-dialog-row
                  v-for="input in inputs"
                  v-bind="input"
                  :key="input.name"
                  v-model="payload[input.name]"
                >
                </main-input-dialog-row>
              </v-col>
            </v-row>
            <expression-builder
              ref="expressionBuilder"
              v-model="payload.filter_expression"
              :mainDataSource.sync="payload.data_source"
              :type="scope"
              :is-data-source-changable="isDataSourceChangable"
              admin
            >
            </expression-builder>
            <layer-adding-permissions
              class="pt-9"
              :value="payload.permissions"
              @input="onInput"
              @mainInput="onMainInput"
              @attachmentsInput="onAttachmentsInput"
              type="features_layer"
            ></layer-adding-permissions>
          </v-form>
        </v-col>
      </template>
      <main-loader v-else></main-loader>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import ExpressionBuilder from '@/components/ExpressionBuilder';
import MainLoader from '@/components/MainLoader';
import LayerAddingPermissions from '@/components/LayerAddingPermissions';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'LayerAddingDialog',
  components: {
    MainLargeDialog,
    MainInputDialogRow,
    ExpressionBuilder,
    MainLoader,
    LayerAddingPermissions,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    scope: {
      type: String,
      default: '',
    },
    layer: {
      type: Object,
      default: undefined,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetched: false,
    loading: false,
    isFiltersVisible: false,
    isValid: false,
    currentLayerObject: undefined,
    filter: undefined,
    payload: {
      name: undefined,
      filter_expression: null,
      style_web: {},
      data_source: undefined,
      permissions: [],
    },
    isDataSourceChangable: false,
  }),
  computed: {
    computedTitle() {
      return this.isEditing ? this.$i18n.t('dialog.title.manageLayer') : this.$i18n.t('dialog.title.addLayer');
    },
    computedAgree() {
      return this.isEditing ? 'dialog.agreeSave' : 'dialog.agreeAdd';
    },
    agreeAction() {
      return this.isEditing ? this.editLayer : this.addLayer;
    },
    groups: get('users/groups'),
    inputs() {
      return [
        {
          name: 'name',
          cols: '12',
          clearable: true,
          align: 'baseline',
          translationPath: 'dialog.body.layerName',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: 35,
          },
        },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (!nV) {
          this.$refs.form?.reset();
          this.$refs.form?.resetValidation();
          this.$refs.expressionBuilder?.reset();
          this.isFetched = false;
          this.isValid = false;
          this.$emit('update:layer', undefined);
        } else {
          this.init();
        }
      },
    },
  },
  methods: {
    getLayerPermissions: call('layers/getLayerPermissions'),
    addLayerAction: call('layers/addLayer'),
    editLayerAction: call('layers/editLayer'),
    getGroups: call('users/getGroups'),
    onInput(e) {
      const index = this.payload.permissions.map(permission => permission.group_id).indexOf(e.group_id);
      this.payload.permissions.splice(index, 1, { ...e });
    },
    onAttachmentsInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, attachments_and_comments_value: v };
      });
    },
    onMainInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, main_value: v };
      });
    },
    getPermissions() {
      return this.$_objectToArray(this.groups)
        .filter(group => !group.is_admin_group)
        .map(group => {
          return { group_id: group.id, main_value: 0, attachments_and_comments_value: 0 };
        });
    },
    async addEditHandler(f, payload) {
      try {
        this.loading = true;
        await f(payload);
        this.loading = false;
        this.computedIsVisible = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async editLayer() {
      const payloadCopy = { ...this.payload };
      delete payloadCopy.data_source;
      await this.addEditHandler(this.editLayerAction, {
        layer_id: this.layer.id,
        scope: this.scope,
        layer_type: 'features_layers',
        body: {
          ...payloadCopy,
        },
      });
    },
    async addLayer() {
      await this.addEditHandler(this.addLayerAction, {
        layer_type: 'features_layers',
        body: {
          ...this.payload,
          layer_scope: this.scope,
        },
      });
    },
    async init() {
      try {
        await this.getGroups();
        if (this.isEditing) {
          const { name, filter_expression, data_source_name, id } = this.layer;
          this.payload = {
            ...this.payload,
            name,
            filter_expression,
            data_source: data_source_name,
            permissions: [],
          };
          this.isDataSourceChangable = false;
          this.payload.permissions = JSON.parse(JSON.stringify((await this.getLayerPermissions(id)).data.data));
        } else {
          this.payload.permissions = JSON.parse(JSON.stringify(this.getPermissions()));
          this.isDataSourceChangable = true;
        }
        this.isFetched = true;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-list-item__title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
}
</style>
